import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  useColorModeValue,
  Icon,
  Container,
  Grid,
  GridItem,
  Image,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FiCpu, FiHardDrive, FiMonitor, FiSettings, FiShoppingCart } from "react-icons/fi";

const Hero: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const accentColor = useColorModeValue("blue.500", "blue.300");

  return (
    <Box
      position="relative"
      minH="90vh"
      bg="green.900"
      overflow="hidden"
      display="flex"
      alignItems="center"
    >
      {/* Tech Background Pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgGradient="radial(circle at center, #1a4731 0%, #0f2a1a 100%)"
        opacity={0.95}
      />

      {/* Circuit Pattern Overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgImage="url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMzAgMzBoMjB2MjBIMzB6IiBmaWxsPSJub25lIiBzdHJva2U9IiM0QjVFQjUiIHN0cm9rZS13aWR0aD0iMSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIvPjwvc3ZnPg==')"
        opacity={0.1}
      />

      {/* Decorative Elements */}
      <Box
        position="absolute"
        top="-20%"
        right="-20%"
        width="60%"
        height="60%"
        bgGradient="radial(circle, green.400 0%, transparent 70%)"
        opacity={0.1}
        filter="blur(40px)"
        transform="rotate(45deg)"
      />
      <Box
        position="absolute"
        bottom="-20%"
        left="-20%"
        width="60%"
        height="60%"
        bgGradient="radial(circle, green.400 0%, transparent 70%)"
        opacity={0.1}
        filter="blur(40px)"
        transform="rotate(-45deg)"
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8} alignItems="center">
          {/* Left Content */}
          <GridItem>
            <VStack align="start" spacing={6}>
              <Badge
                colorScheme="green"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="sm"
              >
                Premium Computer Parts
              </Badge>
              <Text
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="bold"
                color="white"
                lineHeight="shorter"
              >
                Build Your Dream PC with Premium Parts
              </Text>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.300"
                maxW="600px"
              >
                Discover high-performance components from top brands. From processors to graphics cards, we've got everything you need to build your perfect system.
              </Text>
              <HStack spacing={4}>
                <Button
                  as={RouterLink}
                  to="/products"
                  size="lg"
                  colorScheme="green"
                  leftIcon={<FiShoppingCart />}
                >
                  Shop Now
                </Button>
                <Button
                  as={RouterLink}
                  to="/categories"
                  size="lg"
                  variant="outline"
                  color="white"
                  _hover={{ bg: "whiteAlpha.200" }}
                >
                  Browse Categories
                </Button>
              </HStack>
            </VStack>
          </GridItem>

          {/* Right Content - Feature Grid */}
          <GridItem>
            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              <Box
                bg="whiteAlpha.100"
                p={6}
                borderRadius="xl"
                backdropFilter="blur(10px)"
                border="1px"
                borderColor="whiteAlpha.200"
                _hover={{ transform: "translateY(-4px)", transition: "all 0.3s" }}
              >
                <Icon as={FiCpu} w={8} h={8} color="green.400" mb={4} />
                <Text color="white" fontSize="lg" fontWeight="semibold" mb={2}>
                  Latest Processors
                </Text>
                <Text color="gray.300" fontSize="sm">
                  High-performance CPUs from Intel and AMD
                </Text>
              </Box>
              <Box
                bg="whiteAlpha.100"
                p={6}
                borderRadius="xl"
                backdropFilter="blur(10px)"
                border="1px"
                borderColor="whiteAlpha.200"
                _hover={{ transform: "translateY(-4px)", transition: "all 0.3s" }}
              >
                <Icon as={FiHardDrive} w={8} h={8} color="green.400" mb={4} />
                <Text color="white" fontSize="lg" fontWeight="semibold" mb={2}>
                  Fast Storage
                </Text>
                <Text color="gray.300" fontSize="sm">
                  SSDs and HDDs for every need
                </Text>
              </Box>
              <Box
                bg="whiteAlpha.100"
                p={6}
                borderRadius="xl"
                backdropFilter="blur(10px)"
                border="1px"
                borderColor="whiteAlpha.200"
                _hover={{ transform: "translateY(-4px)", transition: "all 0.3s" }}
              >
                <Icon as={FiMonitor} w={8} h={8} color="green.400" mb={4} />
                <Text color="white" fontSize="lg" fontWeight="semibold" mb={2}>
                  Graphics Cards
                </Text>
                <Text color="gray.300" fontSize="sm">
                  Latest GPUs for gaming and work
                </Text>
              </Box>
              <Box
                bg="whiteAlpha.100"
                p={6}
                borderRadius="xl"
                backdropFilter="blur(10px)"
                border="1px"
                borderColor="whiteAlpha.200"
                _hover={{ transform: "translateY(-4px)", transition: "all 0.3s" }}
              >
                <Icon as={FiSettings} w={8} h={8} color="green.400" mb={4} />
                <Text color="white" fontSize="lg" fontWeight="semibold" mb={2}>
                  Peripherals
                </Text>
                <Text color="gray.300" fontSize="sm">
                  Keyboards, mice, and more
                </Text>
              </Box>
            </Grid>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
